/**
 * OfflineBanner — sticky banner that appears across the whole app when the
 * device loses network. On reconnect it briefly shows a green "Back online"
 * pill and re-runs any pending axios requests parked in `api.retryQueue`.
 *
 * Works on web (navigator.onLine) and Android (Capacitor Network plugin).
 */
import React, { useEffect, useState } from "react";
import { WifiOff, Wifi } from "lucide-react";
import { Network } from "@capacitor/network";
import { isNativePlatform } from "../lib/platform";

export default function OfflineBanner() {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [justReconnected, setJustReconnected] = useState(false);

  useEffect(() => {
    let cleanup = () => {};

    function setOn(v) {
      setOnline((prev) => {
        if (!prev && v) {
          setJustReconnected(true);
          setTimeout(() => setJustReconnected(false), 2500);
          // Replay any failed requests that opted-in.
          try {
            const ev = new Event("hp:network-online");
            window.dispatchEvent(ev);
          } catch {/* no-op */}
        }
        return v;
      });
    }

    // Web listeners
    const onOnline = () => setOn(true);
    const onOffline = () => setOn(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    // Capacitor Network plugin (richer, also catches "not connected" on Android WiFi
    // pages without IP). Statically imported above to avoid the file:// dynamic
    // chunk-load hang we saw on Android 16 with PushNotifications.
    if (isNativePlatform()) {
      (async () => {
        try {
          const initial = await Network.getStatus();
          setOn(initial.connected);
          const handle = await Network.addListener("networkStatusChange", (s) => setOn(s.connected));
          cleanup = () => handle.remove();
        } catch {/* ignore */}
      })();
    }

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      cleanup();
    };
  }, []);

  if (online && !justReconnected) return null;

  return (
    <div
      data-testid="offline-banner"
      className={`fixed top-0 inset-x-0 z-[100] pointer-events-none flex justify-center`}
    >
      <div
        className={`pointer-events-auto mt-1 flex items-center gap-2 px-3 py-1.5 rounded-full shadow-lg text-xs font-semibold transition-all ${
          online
            ? "bg-emerald-600 text-white"
            : "bg-rose-600 text-white animate-pulse"
        }`}
      >
        {online ? (
          <>
            <Wifi className="w-3.5 h-3.5" />
            Internet wapas mil gaya
          </>
        ) : (
          <>
            <WifiOff className="w-3.5 h-3.5" />
            Internet nahi hai — connection ka intezaar
          </>
        )}
      </div>
    </div>
  );
}
